<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCampaignDrip } from 'dashboard/composables/useCampaignDrip';
import SmsPartnerAPI from 'dashboard/api/smsPartner';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import CampaignProgressDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignProgressDialog.vue';
import SmsPartnerDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SmsPartnerDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
// Modal central (DS Dialog) aberto via ref — botão do topo OU CTA embaixo da lista.
const campaignDialogRef = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const progressDialogRef = ref(null);
const partnerDialogRef = ref(null);

// Parceiro SMS (operadora Shortcode): o disparo só libera quando conectado/validado.
const partner = ref({ configured: false, validated: false });
const loadPartner = async () => {
  try {
    const { data } = await SmsPartnerAPI.get();
    partner.value = data || { configured: false, validated: false };
  } catch (e) {
    partner.value = { configured: false, validated: false };
  }
};
onMounted(loadPartner);

const SMSCampaigns = computed(() => getters['campaigns/getSMSCampaigns'].value);
const hasNoSMSCampaigns = computed(
  () => SMSCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const { handleStart, handlePause, handleResume, handlePreview } =
  useCampaignDrip();

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
const handleProgress = campaign => progressDialogRef.value?.open(campaign);
const openPartner = () => partnerDialogRef.value?.open();
// "Inserir saldo": abre o painel da operadora Short Code (recarga de crédito).
const openShortcodePortal = () =>
  window.open('https://app.smsshortcode.com.br/', '_blank', 'noopener');

// Gating: só abre o criar campanha se o parceiro estiver validado.
const handleCreateClick = () => {
  if (!partner.value.validated) {
    useAlert('Configure e valide o parceiro SMS (Shortcode) antes de criar campanhas.');
    openPartner();
    return;
  }
  campaignDialogRef.value?.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.SMS.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.SMS.NEW_CAMPAIGN')"
    @click="handleCreateClick"
  >
    <!-- Status do parceiro SMS (Shortcode) -->
    <div
      class="flex items-center justify-between gap-3 p-3 mb-4 rounded-xl bg-n-alpha-2 border border-n-weak"
    >
      <div class="flex items-center gap-2 text-sm">
        <span
          :class="
            partner.validated
              ? 'i-ri-checkbox-circle-line text-n-teal-11'
              : 'i-ri-error-warning-line text-n-amber-11'
          "
        />
        <span v-if="partner.validated" class="text-n-slate-11">
          Parceiro SMS (Shortcode) conectado e validado.
        </span>
        <span v-else class="text-n-slate-11">
          Parceiro SMS (Shortcode) não configurado — configure para liberar os disparos.
        </span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <Button
          v-if="partner.configured"
          label="Inserir saldo"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-ri-wallet-line"
          @click="openShortcodePortal"
        />
        <Button
          :label="partner.configured ? 'Editar parceiro' : 'Configurar parceiro'"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-ri-settings-line"
          @click="openPartner"
        />
      </div>
    </div>

    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="!hasNoSMSCampaigns">
      <CampaignList
        :campaigns="SMSCampaigns"
        @delete="handleDelete"
        @start="handleStart"
        @pause="handlePause"
        @resume="handleResume"
        @preview="handlePreview"
        @progress="handleProgress"
      />
      <!-- Criar nova campanha logo abaixo das campanhas recentes -->
      <div v-if="partner.validated" class="flex justify-center mt-4">
        <Button
          :label="t('CAMPAIGN.SMS.NEW_CAMPAIGN')"
          icon="i-ri-add-line"
          size="sm"
          @click="handleCreateClick"
        />
      </div>
    </template>
    <SMSCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.SMS.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.SMS.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <WhatsAppCampaignDialog
      ref="campaignDialogRef"
      title="Criar campanha de SMS"
      inboxes-getter="inboxes/getSmsShortcodeInboxes"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignProgressDialog ref="progressDialogRef" />
    <SmsPartnerDialog ref="partnerDialogRef" @saved="loadPartner" />
  </CampaignLayout>
</template>
